import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { MessagingService } from 'src/app/core/messaging/messaging.service';
import { TranslationService } from 'src/app/core/translation/translation.service';
import { ButtonType } from 'src/app/shared/common/components/buttons/button';
import { NotificationType } from 'src/app/shared/common/components/gsp-notification/gsp-notification.component';
import { FeaturesStreamsService } from 'src/app/shared/common/current-features/features-streams.service';
import { TaskFeaturesStreamsService } from 'src/app/shared/common/current-features/task-features-streams.service';
import { LayersStore } from 'src/app/shared/common/current-layers/layers-store.service';
import { ProjectStreamService } from 'src/app/shared/common/current-project/project-stream.service';
import { TasksStore } from 'src/app/shared/common/current-tasks/tasks-store.service';
import { TasksStreamsService } from 'src/app/shared/common/current-tasks/tasks-streams.service';
import { TemplatesStoreService } from 'src/app/shared/common/current-templates/templates-store.service';
import { CurrentUserStreamService } from 'src/app/shared/common/current-user/current-user-stream.service';
import { FullFeatureFilter } from 'src/app/shared/common/feature-filter/full-feature-filter';
import { Task, TaskCreationMode, TaskPanelTabs, TaskStatus } from 'src/app/shared/common/task/task';
import { CloneUtils } from 'src/app/shared/common/utility/clone-utils';
import { GeometryTypes } from 'src/app/shared/common/utility/geometry-utils';
import { CachedFeatureService } from 'src/app/shared/map-data-services/feature/cached-feature.service';
import { Feature } from 'src/app/shared/map-data-services/feature/feature';
import { FeatureService } from 'src/app/shared/map-data-services/feature/feature.service';
import { Layer } from 'src/app/shared/map-data-services/layer/layer';
import { LayerStyleGenerator } from 'src/app/shared/map-data-services/layer/layer-style-generator';
import { PermissionType } from 'src/app/shared/map-data-services/mapWorkspace/permission';
import { Project } from 'src/app/shared/map-data-services/project/project';
import { Template } from 'src/app/shared/template-services/template';
import { Actor } from 'src/app/shared/user/actor';
import { User, UserRole } from 'src/app/shared/user/user';

// class representing feature on the task detail panel
class TaskPanelLayer {
    templateSeriesId: string;
    layer: Layer;
    layerColor: string;
    isLayerRemoved: boolean;
    featureIds: string[];
}

@Component({
    selector: 'task-detail-panel',
    templateUrl: './task-detail-panel.component.html'
})
export class TaskDetailPanelComponent implements OnInit, OnDestroy {
    @Input()
    taskId: string;

    @Output()
    setTaskPanelTab = new EventEmitter<TaskPanelTabs>();

    // expose enums to template
    public ButtonType = ButtonType;
    public NotificationType = NotificationType;
    public PermissionType = PermissionType;
    public TaskCreationMode = TaskCreationMode;
    public TaskStatus = TaskStatus;

    private readonly destroyed = new Subject<void>();

    public currentProject: Project;
    public currentUserId: string;
    public task: Task = null;
    public numberOfNonTemplatedFeaturesExcluded = 0;
    public editMode = false;
    public deleteTaskConfirmation = false;
    public closeOrReopenTaskConfirmation = false;
    public disableEdit = true;
    public taskTitleRequired = false;
    public originalTask: Task = null;
    public taskCreationMode: TaskCreationMode;
    public loading = true;
    public taskFeaturesLoading = false;
    public allPotentialAssignees: User[] = [];
    public layerKeyToMapWorkspaceLayer: { [key: string]: Layer };
    public templatesBySeriesId: { [templateId: string]: Template } = {};
    public templateSeriesIdToTaskPanelLayer: { [key: string]: TaskPanelLayer } = {};
    public taskPanelLayers: TaskPanelLayer[] = [];
    public isRemoveFeatureInProgress = false;
    public modifyPermission = false;
    public currentProjectAssignees: Actor[] = [];
    public taskFormLayers: Layer[] = [];
    private taskFeatures: Feature[] = [];
    public showAssignedUsersPanel = false;
    public maxTaskFeatureCount = 5000;

    constructor(
        private projectStream: ProjectStreamService,
        private currentUserStream: CurrentUserStreamService,
        private translate: TranslationService,
        private tasksStore: TasksStore,
        private taskFeaturesStreams: TaskFeaturesStreamsService,
        private featuresStreams: FeaturesStreamsService,
        private cachedFeatureService: CachedFeatureService,
        private layersStore: LayersStore,
        private templatesStore: TemplatesStoreService,
        private messaging: MessagingService,
        private featureService: FeatureService,
        private tasksStreams: TasksStreamsService
    ) {}

    async ngOnInit(): Promise<void> {
        this.currentUserId = this.currentUserStream.currentUser.id;
        this.currentProjectAssignees = this.tasksStore.getAllAssignees();
        this.layerKeyToMapWorkspaceLayer = this.layersStore.getLayerKeyToLayerMap();

        this.projectStream.currentProjectStream
            .pipe(takeUntil(this.destroyed))
            .subscribe(project => (this.currentProject = project));
        if (this.taskId) {
            this.resetTaskPanelOnCancelEdit();
            this.task = await this.tasksStore.getTaskById(this.taskId);
        } else {
            this.task = await this.tasksStore.createNewTask();
            this.taskCreationMode = TaskCreationMode.NEW;
        }

        if (this.task) {
            await this.setModifyPermission();
            this.disableEdit = this.task.status === TaskStatus.CLOSED;
            if (this.taskCreationMode === TaskCreationMode.NEW) {
                this.featuresStreams.selectedFeaturesStream.pipe(takeUntil(this.destroyed)).subscribe(features => {
                    this.task.features = this.tasksStore.getTaskFeaturesFromSelectedFeatures(features);
                    this.loadLayersForTaskFeatures();
                });
            } else {
                this.loadLayersForTaskFeatures();
            }
            this.subscribeToPendingLoadingTaskStream();
            this.originalTask = CloneUtils.cloneDeep(this.task);
        }
        this.loading = false;
    }

    private resetTaskPanelOnCancelEdit() {
        this.tasksStreams.selectedTasksStream
            .pipe(
                takeUntil(this.destroyed),
                filter(() => this.taskCreationMode === TaskCreationMode.EDIT)
            )
            .subscribe(async () => {
                this.taskCreationMode = null;
                this.loading = true;
                await this.tasksStore.getTaskById(this.taskId).then(task => {
                    this.task = task;
                    this.loadLayersForTaskFeatures();
                    this.loading = false;
                });
            });
    }

    closeOrReopenTaskAction = () => this.closeOrReopenTask().then(() => this.back());

    public async closeOrReopenTask(): Promise<void> {
        if (!this.modifyPermission) {
            this.messaging.showWarning(this.translate.instant('PERMISSION_DENIED'));
            return;
        }

        const newStatus = this.task.isClosed
            ? this.task.percentageComplete > 0
                ? TaskStatus.IN_PROGRESS
                : TaskStatus.NEW
            : TaskStatus.CLOSED;

        const newTabStatus = newStatus === TaskStatus.CLOSED ? TaskPanelTabs.CLOSED : TaskPanelTabs.ACTIVE;

        this.loading = true;

        if (newStatus === TaskStatus.CLOSED) {
            await this.tasksStore.closeTask(this.task);
        } else {
            await this.tasksStore.reopenTask(this.task);
        }
        this.task.isClosed = newStatus === TaskStatus.CLOSED;
        this.task.visible = newStatus !== TaskStatus.CLOSED;

        const visibleTasks = this.tasksStreams.visibleTasksStream.getValue();
        this.tasksStreams.setVisibleTasks(visibleTasks.filter(x => x.visible));

        if (this.task.isClosed) {
            this.taskFeaturesStreams.getFeaturesByTask(this.task).then(data => {
                this.task.taskFeatures = data;
                this.tasksStreams.deferHideTasksStream.next([this.task]);
            });
        }

        // Refresh tabs
        this.tasksStore.loadCurrentWorkspaceTasksBySearch(TaskPanelTabs.CLOSED, '');
        this.tasksStore.loadCurrentWorkspaceTasksBySearch(TaskPanelTabs.ACTIVE, '');

        this.setTaskPanelTab.emit(newTabStatus);
    }

    private subscribeToPendingLoadingTaskStream(): void {
        // update task.taskFeatures on task feature visibility changes
        this.tasksStreams.pendingLoadingTaskStream.pipe(takeUntil(this.destroyed)).subscribe(pendingTaskObj => {
            if (pendingTaskObj && pendingTaskObj.loading) {
                if (this.task.id === pendingTaskObj.task.id) {
                    this.task = pendingTaskObj.task;
                }
            }
        });
    }

    public get exceedsLimit(): boolean {
        return this.task && this.task.features && this.task.features.length > this.maxTaskFeatureCount;
    }

    // Ensure all layers, including those no longer in the workspace, are loaded.
    private async loadLayersForTaskFeatures(): Promise<void> {
        let layerKeyToLayerMap = this.layersStore.getLayerKeyToLayerMap();
        let promises: Promise<void>[] = [];
        this.templateSeriesIdToTaskPanelLayer = {};
        this.taskFeatures = [];
        this.taskFeaturesLoading = true;

        const cachedFeatures: Feature[] = [];
        const uncachedFeatureIds: string[] = [];
        const featureIdToTaskFeatureMap: { [featureId: string]: any } = {};

        this.task.features.forEach(taskFeature => {
            featureIdToTaskFeatureMap[taskFeature.featureId] = taskFeature;

            if (!this.templateSeriesIdToTaskPanelLayer[taskFeature.templateSeriesId]) {
                this.templateSeriesIdToTaskPanelLayer[taskFeature.templateSeriesId] = {
                    templateSeriesId: taskFeature.templateSeriesId,
                    layer: null,
                    layerColor: '#000000',
                    isLayerRemoved: false,
                    featureIds: []
                };

                // Load template for each unique template series
                let loadTemplatePromise = this.templatesStore
                    .getTemplateBySeriesId(taskFeature.templateSeriesId)
                    .then(template => {
                        this.templatesBySeriesId[taskFeature.templateSeriesId] = template;
                    });
                promises.push(loadTemplatePromise);
            }

            // Check cache first to avoid unnecessary API calls.
            let feature = this.cachedFeatureService.getFeature(taskFeature.featureId);
            if (feature && !feature.deleted) {
                cachedFeatures.push(feature);
            } else {
                uncachedFeatureIds.push(taskFeature.featureId);
            }

            this.templateSeriesIdToTaskPanelLayer[taskFeature.templateSeriesId].featureIds.push(taskFeature.featureId);
        });

        // Batch load uncached features using paginated getFeatures().
        if (uncachedFeatureIds.length > 0) {
            const filter = new FullFeatureFilter();
            filter.selectedFeatureIds = uncachedFeatureIds;

            const batchLoadPromise = this.featureService
                .getFeatures(this.currentProject.id, filter, uncachedFeatureIds.length, (pageFeatures: Feature[]) => {
                    // Incremental update: add features as they're loaded page by page.
                    this.taskFeatures.push(...pageFeatures);
                    this.task.taskFeatures = [...this.taskFeatures];
                })
                .then(() => {
                    this.task.taskFeatures = [...this.taskFeatures];
                });

            promises.push(batchLoadPromise);
        }

        this.taskFeatures.push(...cachedFeatures);
        this.task.taskFeatures = [...this.taskFeatures];

        await Promise.all(promises);

        // Load layers for all features, avoiding duplicate layer loads.
        const layerPromises: Promise<void>[] = [];
        const processedLayerKeys = new Set<string>();

        this.taskFeatures.forEach(feature => {
            if (!feature || !feature.id) return;

            const taskFeature = featureIdToTaskFeatureMap[feature.id];
            if (!taskFeature) return;

            let layerKey = feature.getLayerKey();
            let isLayerRemoved = !Boolean(layerKeyToLayerMap[layerKey]);

            if (layerKey) {
                const layerKeyCache = `${layerKey}_${taskFeature.templateSeriesId}`;

                // Avoid loading the same layer multiple times
                if (!processedLayerKeys.has(layerKeyCache)) {
                    processedLayerKeys.add(layerKeyCache);

                    let layerId = layerKey;
                    let loadLayerPromise = this.layersStore.getMapWorkspaceLayerByIdPromise(layerId).then(layer => {
                        let taskPanelLayer = this.templateSeriesIdToTaskPanelLayer[taskFeature.templateSeriesId];
                        taskPanelLayer.layer = layer;
                        taskPanelLayer.layerColor = layer.getLayerColor();
                        taskPanelLayer.isLayerRemoved = isLayerRemoved;
                    });
                    layerPromises.push(loadLayerPromise);
                }
            }
        });

        await Promise.all(layerPromises);

        // Update features with their layer properties so layerId is set.
        this.taskFeatures.forEach(feature => {
            if (!feature || !feature.id) return;

            const taskFeature = featureIdToTaskFeatureMap[feature.id];
            if (!taskFeature) return;

            const taskPanelLayer = this.templateSeriesIdToTaskPanelLayer[taskFeature.templateSeriesId];
            if (taskPanelLayer && taskPanelLayer.layer) {
                feature.addLayerProperties(taskPanelLayer.layer);
            }
        });

        this.taskPanelLayers = Object.values(this.templateSeriesIdToTaskPanelLayer);
        this.taskFeaturesLoading = false;
    }

    public getLayerName(layer: Layer): string {
        return layer ? layer.layerName : this.translate.instant('TC.Common.NoneParenthesis');
    }

    public getLayerOutlineColor(layer: Layer): string {
        return LayerStyleGenerator.getOutlineColorFromStyle(layer.style, layer.geometryType, layer.color);
    }

    public getTemplateGeometryType(templateSeriesId: string): string {
        let template = this.templatesBySeriesId[templateSeriesId];
        return template ? template.geometryType : GeometryTypes.NONE;
    }

    public getTaskFormsByLayer(layer: Layer): Feature[] {
        return this.task.taskFeatures.filter(feature => feature.id !== null && feature.layerId === layer.id);
    }

    public back() {
        this.tasksStreams.setSelectedTasks([]);
        this.tasksStreams.disableMapviewerStream.next(true);
        this.taskCreationMode = null;
    }

    private async setModifyPermission(): Promise<void> {
        const user = await this.projectStream.getUserWithRolesForCurrentProject(this.currentUserId);
        this.modifyPermission = user.role === UserRole.ADMIN || this.task.createdBy === this.currentUserId;
    }

    public editTask() {
        if (!this.modifyPermission) {
            this.messaging.showWarning(this.translate.instant('PERMISSION_DENIED'));
            return;
        }

        this.taskCreationMode = TaskCreationMode.EDIT;

        if (this.tasksStreams.disableMapviewerStream.getValue()) {
            this.tasksStreams.disableMapviewerStream.next(true);
        }
    }

    public deleteAction = () => this.deleteTask().then(() => this.back());

    private deleteTask(): Promise<void> {
        if (!this.modifyPermission) {
            this.messaging.showWarning(this.translate.instant('PERMISSION_DENIED'));
            return Promise.resolve();
        }

        return this.tasksStore.deleteTask(this.task).then(
            () => {
                this.messaging.showSuccess(this.translate.instant('TC.TodosTask.Message.Deleted'));
                this.originalTask = CloneUtils.cloneDeep(this.task);
                // hide the selection of task features in the mapViewer after deletion if visible
                if (this.task.visible) {
                    this.tasksStreams.deferHideTasksStream.next([this.task]);
                }
            },
            () => {
                this.messaging.showError(this.translate.instant('TC.Common.ErrorWhileDeleting'));
            }
        );
    }

    public toggleAssignedUsersPanel() {
        this.showAssignedUsersPanel = !this.showAssignedUsersPanel;
    }

    public selectAllForms() {
        let taskFeatureIds = this.task.features.map(feature => feature.featureId);
        this.featuresStreams.replaceOnSelectedFeatures(taskFeatureIds);
        this.tasksStreams.disableMapviewerStream.next(false);
    }

    ngOnDestroy(): void {
        this.destroyed.next(null);
    }
}
